import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { MountTransition } from '@/components/MonitoringPanel/components/MountTransition';
import { Modal } from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import { SidePanelAnimation } from './styled';

type Props = {
  onClose: () => void;
  children?: ReactNode;
};

export const SidePanel = ({ onClose, children }: Props) => {
  const ANIMATION_DURATION = 300;
  const exitRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        exitRef.current?.();
      }
    };

    window.addEventListener('keydown', handleEscKey);

    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  return (
    <MountTransition onExited={onClose} duration={ANIMATION_DURATION}>
      {({ state, exit }) => {
        const isOpen = state === 'entering' || state === 'entered';
        //save the exit function to a ref so we can call when the "Esc key pressed" event is triggered
        exitRef.current = exit;

        return (
          <Modal isNaked={true}>
            <SidePanelAnimation
              className="absolute inset-0 left-auto w-full max-w-[420px] bg-p-grey-180"
              isOpen={isOpen}
              duration={ANIMATION_DURATION}
            >
              {/* Setting the <Modal> component z-index to 99 will render the notes modal with z-index: 100 on top*/}
              {isOpen && (
                <style>{`[data-cy-id="modal-content"] { z-index: 400; }`}</style>
              )}
              <div className="bg-p-grey-180 pb-[calc(62px+env(safe-area-inset-bottom))] lg:pb-0">
                <div className="fixed top-0 z-10 grid h-[calc(48px+env(safe-area-inset-top))] w-full max-w-[420px] grid-cols-[max-content_max-content] items-center justify-between bg-p-grey-180 px-x2 pt-[env(safe-area-inset-top)]">
                  <div className="text-lg text-softer">Last 30d updates</div>
                  <button type="button" onClick={exit} aria-label="close">
                    <CrossSolid fill="darkGray" />
                  </button>
                </div>
                <div className="mt-[calc(48px+env(safe-area-inset-top))] grid gap-x1 overflow-y-auto bg-p-grey-180 lg:mx-x2">
                  {children}
                </div>
              </div>
            </SidePanelAnimation>
          </Modal>
        );
      }}
    </MountTransition>
  );
};
